import { css } from 'aphrodite';
import { Abstract, ModalWrapper, SharedCarousel, TrackSession, VideoModal } from 'components';
import { useScrollToTopOnMount } from 'hooks';
import useSessionVideos from 'hooks/useSessionVideos';
import { VideoData } from 'models';
import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { RootState } from 'redux/store';
import api from 'util/api';
import { GAEventAction, GAEventCategory, GAEventLabel, trackGoogleEvent } from 'util/google-helpers';
import { makeSlug } from 'util/helpers';
import { style } from './style';

interface TechnicalTracksParams {
  categoryId?: string;
}

interface ICategory {
  id: string;
  label: string;
}

const DEFAULT_ROUTE = '/technical-tracks';

export const TechnicalTracks: FC<{}> = () => {
  useScrollToTopOnMount();

  const cmsCategories = useSelector((state: RootState) => state.widget.cms.text.header.technical_tracks_categories);
  const copy = useSelector((state: RootState) => state.widget.cms.text.technical_session);
  const categories: ICategory[] = cmsCategories.map((label: string) => {
    return { id: makeSlug(label), label };
  });
  const styles = style({ tabs: categories.length });
  const { categoryId = '' } = useParams<TechnicalTracksParams>();

  const selectedCategory = getCategory(categoryId);

  const technicalTracksVideos = useSessionVideos(DEFAULT_ROUTE);
  const technicalTracksVideosByCategory = buildVideosByCategory(selectedCategory, technicalTracksVideos);
  const [videoModalData, setVideoModalData] = useState<VideoData>();
  const [isAbstractModalOpen, setAbstractModalOpen] = useState<boolean>(false);
  const [abstract, setAbstract] = useState<any | undefined>(undefined);

  function getCategory(id: string) {
    if (!id) return categories[0] || null;
    return categories.find((category) => category.id === id);
  }

  function buildVideosByCategory(category: ICategory | undefined, videos: Array<VideoData> = []) {
    const videosByCategory: Record<string, Array<VideoData>> = {};
    if (category) {
      videosByCategory[category.id] = videos.filter((video) => video.page.includes(category.id));
    } else {
      categories.forEach((category) => {
        videosByCategory[category.id] = videos.filter((video) => video.page.includes(category.id));
      });
    }
    return videosByCategory;
  }

  const getVideosByCategory = (category: ICategory | undefined): Array<VideoData> => {
    if (!category) return [];
    else return technicalTracksVideosByCategory[category.id];
  };

  const getContent = (categoryId: string = ''): JSX.Element | Array<JSX.Element | null> | null => {
    const category = getCategory(categoryId);
    if (category) return getCategorySection(category);
    return null;
  };

  // Sort videos by company name
  // Note: company name will be added under the speaker_titles property for CMS simplicity
  const sortVideosByCompanyName = (videos: VideoData[]) => {
    return videos.sort((a, b) => a.speaker_titles[0].localeCompare(b.speaker_titles[0]));
  };

  const getCategorySection = (category: ICategory, id?: number): JSX.Element | null => {
    const videos = technicalTracksVideosByCategory[category.id];
    sortVideosByCompanyName(videos);
    if (!videos || !videos.length) return null;
    return (
      <div key={id} className={css(styles.content)}>
        <div className={css(styles.videoTileContainer)}>{renderVideoTiles(videos)}</div>
      </div>
    );
  };

  const renderVideoTiles = (videos: Array<VideoData>) => {
    return videos.map((data: VideoData, index: number) => {
      return (
        <TrackSession
          key={index}
          video={data}
          onAbstractAndBioClick={() => handleAbstractClick(data.abstract_url || '')}
          onVideoClick={() => handleVideoClick(data)}
        />
      );
    });
  };

  function getCategoryFromPagePath(page: string = ''): ICategory | undefined {
    if (!page) return undefined;
    return categories.find((category) => page.includes(category.id));
  }

  const handleCategoryClick = (id: string) => {};

  const handleAbstractClick = async (url: string) => {
    if (!url) return;
    const response = await api.fetchAbstract(url);
    if (!response) return;
    setAbstract(response);
    setAbstractModalOpen(true);
    trackGoogleEvent(GAEventCategory.SITE, GAEventAction.CLICK, GAEventLabel.SPEAKER_BIO);
  };

  const handleVideoClick = (video: VideoData) => {
    setVideoModalData(video);
  };

  const getCategoryTabs = (): JSX.Element => {
    return (
      <div className={css(styles.tabs)}>
        {categories.map((category: ICategory, index: number) => {
          const isSelected = selectedCategory?.id === category.id;
          const classnames = isSelected ? css(styles.tab, styles.selected) : css(styles.tab);
          return (
            <Link
              key={index}
              to={!categoryId ? DEFAULT_ROUTE + '/' + category.id : category.id}
              className={classnames}
              onClick={() => handleCategoryClick(category.id)}>
              {category.label}
            </Link>
          );
        })}
      </div>
    );
  };

  const getFeaturedTalks = () => {
    return technicalTracksVideos.filter((video) => video.page.includes('tsmc'));
  };

  return (
    <>
      <main className={css(styles.main)}>
        <section className={css(styles.featuredSessions)}>
          <div className={css(styles.featuredHeader)}>
            <h1>{copy.featured_talks.title}</h1>
          </div>
          <div className={css(styles.content)}>
            <div className={css(styles.videoTileContainer, styles.featuredVideoTileContainer)}>
              {renderVideoTiles(getFeaturedTalks())}
            </div>
          </div>
        </section>
        <section className={css(styles.categories)}>
          {getCategoryTabs()}
          {getContent(selectedCategory?.id)}
        </section>
        <div className={css(styles.carousel)}>
          <SharedCarousel />
        </div>
      </main>
      {videoModalData && (
        <VideoModal
          active={!!videoModalData}
          video={videoModalData}
          videos={getVideosByCategory(getCategoryFromPagePath(videoModalData.page))}
          autoplay
          autoplayNext
          onRequestClose={() => setVideoModalData(undefined)}
        />
      )}
      <ModalWrapper
        isOpen={isAbstractModalOpen}
        options={{ fillSmallerScreens: true, preventScroll: true }}
        onRequestClose={() => setAbstractModalOpen(false)}
        onAfterClose={() => setAbstract(undefined)}
        customStyles={{
          modalContent: {
            desktop: { padding: '72px 21px 36px 96px' },
            tablet: { padding: '90px 21px 36px 96px' },
            mobile: { padding: '58px 15px 20px 20px' },
          },
        }}>
        {abstract && (
          <Abstract
            title={abstract.presentation_title}
            abstract={abstract.presentation_abstract}
            speakerDetails={abstract.speakers}
            cmsData={{ speakers: 'Speakers', experience: 'Experience' }}
          />
        )}
      </ModalWrapper>
    </>
  );
};
